import { INVOICE_ORIGINS, INVOICE_STATUSES } from "../types/invoices.types.ts"

const LOCKED_STATUSES: number[] = [
  INVOICE_STATUSES.ISSUED,
  INVOICE_STATUSES.PARTIALLY_PAID,
  INVOICE_STATUSES.PAID,
  INVOICE_STATUSES.VOID
]

interface LockableInvoice {
  status?: number | null
  origin?: number | null
  is_locked?: boolean
}

interface EditableInvoice {
  status?: number | null
  origin?: number | null
  is_generated?: boolean
  is_editable?: boolean
}

export const isInvoiceGenerated = (record?: EditableInvoice | null): boolean =>
  record?.origin === INVOICE_ORIGINS.GENERATED || Boolean(record?.is_generated)

/**
 * Whether the invoice's own fields can still be corrected.
 *
 * The server's `is_editable` when it sends one. A backend that predates it
 * still refuses every edit to a locked invoice, external ones included, so the
 * fallback follows that backend's own lock rather than offering an edit it
 * would reject.
 */
export const isInvoiceEditable = (
  record?: (EditableInvoice & LockableInvoice) | null
): boolean => {
  if (!record) return false
  if (typeof record.is_editable === "boolean") return record.is_editable
  return !isInvoiceLocked(record)
}

/**
 * An invoice in the lifecycle proper is an immutable document — corrections go
 * through void + reissue.
 *
 * `status` arrives overwritten with the derived OVERDUE on any past-due row, so
 * the stored status is not recoverable here and the server's flag is the only
 * reliable signal. The fallback covers payloads from a backend that predates
 * the field; it locks generated rows on origin (unaffected by the OVERDUE
 * rewrite) and external ones on status, which is wrong only while a past-due
 * external invoice is rendered against an old backend.
 */
export const isInvoiceLocked = (record?: LockableInvoice | null): boolean => {
  if (!record) return false
  if (typeof record.is_locked === "boolean") return record.is_locked
  return (
    (record.origin === INVOICE_ORIGINS.GENERATED &&
      record.status !== INVOICE_STATUSES.DRAFT) ||
    LOCKED_STATUSES.includes(record.status ?? -1)
  )
}
